import { storeToRefs } from 'pinia';

import { DeploymentState } from '@clovyr/pollen';
import { getAllQueryParams } from '@clovyr/pollen/http/qs';
import { getServices } from '@clovyr/pollen/remote_exec/docker';

import { useUserFlagsStore } from '@/stores/user_flags';

import { useAppStatus } from './useAppStatus';
import { useRemoteExec } from './useRemoteExec';

const serviceCache: Record<string, string[]> = {};

interface ServiceOption {
  name: string;
  value: string;
}

export const useDocker = (deploymentID: Ref<string>, addRoot?: boolean) => {
  const { deployment, app } = useAppStatus(deploymentID);
  const userFlagStore = useUserFlagsStore();
  const { canAccessInternalOnlyFeatures } = storeToRefs(userFlagStore);

  const rawServices = ref<string[]>([]);
  const services = ref<string[]>([]);
  const selectedService = ref('');

  const cleanServiceName = computed(() => {
    const v = selectedService.value;
    if (v.startsWith('service: ')) {
      return v.slice(9);
    }
    return v;
  });

  const serviceOptions = computed<ServiceOption[]>(() => {
    let svcs = services.value;
    if (addRoot) {
      svcs = ['<instance>', ...svcs];
    }
    return svcs.map((s) => ({ name: s, value: s }));
  });

  function setDefaultService() {
    if (!selectedService.value && services.value.length) {
      // check for a service in the URL
      const q = getAllQueryParams();
      const target = q.get('target');
      if (target && services.value.includes(target)) {
        selectedService.value = target;
      } else {
        selectedService.value = serviceOptions.value?.[0]?.name ?? 'error!';
      }
    }
  }

  async function refreshServices() {
    if (deployment.value?.state !== DeploymentState.Running) {
      console.warn('deployment not running or key not found');
      return;
    }

    const { creds, ensureHostLogin } = useRemoteExec(deployment.value, app.value);
    if (!creds.value.accessKey || !(await ensureHostLogin())) {
      console.warn('host login failed');
      return;
    }
    let svcs = await getServices(creds.value.host, creds.value.accessKey, deployment.value.appID);

    if (!canAccessInternalOnlyFeatures.value) {
      svcs = svcs.filter((s) => !s.startsWith('clovyr-'));
    }

    rawServices.value = svcs;
    services.value = svcs.map((s) => `service: ${s}`);

    setDefaultService();
    serviceCache[deploymentID.value] = services.value;
  }

  const clovyrAppPath = computed(() => {
    return `/opt/clovyr/apps/${app.value.metadata.id}`;
  });

  /**
   * Execute a script on the remote host
   *
   * @param script - script to execute on the remote host
   * @returns
   */
  const exec = async (script: string) => {
    if (deployment.value?.state !== DeploymentState.Running) {
      console.warn('deployment not running or key not found');
      throw new Error('Deployment is not ready for execution');
    }

    const { execWithAsyncOutput } = useRemoteExec(deployment.value, app.value);
    const res = await execWithAsyncOutput(script);
    if (res.exitCode !== 0) {
      throw new Error(`Execution failed with exit code ${res.exitCode}: ${res.output}`);
    }
    return res;
  };

  watch(selectedService, (v, ov) => {
    if (ov && v) {
      const q = getAllQueryParams();
      q.set('target', v);
      window.location.hash = `#${q.toString().replace('=&', '&')}`;
    }
  });

  onMounted(async () => {
    if (serviceCache[deploymentID.value]) {
      services.value = serviceCache[deploymentID.value]!;
      setDefaultService();
    } else {
      void refreshServices();
    }
  });

  return {
    rawServices,
    services,
    selectedService,
    serviceOptions,
    cleanServiceName,

    clovyrAppPath,
    exec,

    refreshServices,
  };
};
